import { createApi } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';

import { pickAndRename } from 'utils/objectUtils';

import getCustomBaseQuery from '../customBaseQuery';
import { BE_TO_FE_INTERFACES, FE_TO_BE_INTERFACES } from './interfaces';
import axiosInstance from 'utils/axios.js';
import { FETCH_PER_PAGE_COUNT } from '_api/constants.js';

const attributesAdapter = createEntityAdapter({
  selectId: attribute => attribute.uuid,
});
const optionsAdapter = createEntityAdapter({
  selectId: option => option.uuid,
});
const attributesInitialState = attributesAdapter.getInitialState();
const optionsInitialState = optionsAdapter.getInitialState();

export const attributesApi = createApi({
  reducerPath: 'attributesApi',
  keepUnusedDataFor: 60 * 5,
  baseQuery: getCustomBaseQuery(),
  tagTypes: ['Attributes', 'Options'],
  endpoints: builder => ({
    getAttributes: builder.query({
      providesTags: ['Attributes'],
      async queryFn(_, _queryApi, _extraOptions, fetchWithBQ) {
        const { data: { meta: { total } } } = await fetchWithBQ({ url: `/attributes?per_page=1&page=1` });;
        const numberOfRequests = Math.ceil(total / FETCH_PER_PAGE_COUNT);

        const promises = [];
        for (let i = 0; i < numberOfRequests; i++) {
          promises.push(axiosInstance.get(`/attributes?per_page=${FETCH_PER_PAGE_COUNT}&page=${i + 1}`));
        }

        const response = await Promise.all(promises);
        const attributes = response.flatMap(res => res.data.data);

        const normalizedData = attributesAdapter.setAll(
          attributesInitialState,
          attributes.map(attribute => pickAndRename(attribute, BE_TO_FE_INTERFACES.Attribute)),
        );

        return {
          data: {
            entities: normalizedData.entities,
            ids: normalizedData.ids,
          },
        };
      },
    }),
    getPaginatedAttributes: builder.query({
      async queryFn(params, _queryApi, _extraOptions, fetchWithBQ) {
        const response = await fetchWithBQ({ url: '/attributes', params });

        const normalizedData = attributesAdapter.setAll(
          attributesInitialState,
          response.data.data.map(attribute => pickAndRename(attribute, BE_TO_FE_INTERFACES.Attribute)),
        );

        return {
          data: {
            data: response.data.data.map(attribute => pickAndRename(attribute, BE_TO_FE_INTERFACES.Attribute)),
            entities: normalizedData.entities,
            ids: normalizedData.ids,
            meta: pickAndRename(response.data.meta, BE_TO_FE_INTERFACES.Meta),
          },
        };
      },
      providesTags: ['Attributes'],
    }),
    createAttribute: builder.mutation({
      query: newAttribute => ({
        url: '/attributes',
        method: 'POST',
        data: pickAndRename(newAttribute, FE_TO_BE_INTERFACES.Attribute),
      }),
      invalidatesTags: ['Attributes'],
    }),
    updateAttribute: builder.mutation({
      query: ({ attributeId, ...rest }) => ({
        url: `/attributes/${attributeId}`,
        method: 'PUT',
        data: rest,
      }),
      invalidatesTags: (result, error, { attributeId }) => [
        { type: 'Attributes' },
        { type: 'Options', id: attributeId },
      ],
    }),
    deleteAttribute: builder.mutation({
      query: attributeId => ({
        url: `/attributes/${attributeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Attributes'],
    }),
    getAttributeOptions: builder.query({
      async queryFn(attributeId, _queryApi, _extraOptions, fetchWithBQ) {
        const { data: { meta: { total } } } = await fetchWithBQ(
          { url: `/attributes/${attributeId}/attribute-options?per_page=5000&page=1` },
        );
        const numberOfRequests = Math.ceil(total / 5000);

        const promises = [];
        for (let i = 0; i < numberOfRequests; i++) {
          promises.push(axiosInstance.get(
            `/attributes/${attributeId}/attribute-options?per_page=5000&page=${i + 1}`,
          ));
        }

        const response = await Promise.all(promises);
        const options = response.flatMap(res => res.data.data);

        const normalizedData = optionsAdapter.setAll(
          optionsInitialState,
          options.map(option => pickAndRename(option, BE_TO_FE_INTERFACES.Option)),
        );

        return {
          data: {
            entities: normalizedData.entities,
            ids: normalizedData.ids,
          },
        };
      },
      providesTags: (result, error, attributeId) => [{ type: 'Options', id: attributeId }],
    }),
    getExternalAttributeOptions: builder.query({
      async queryFn(attributeId, _queryApi, _extraOptions, fetchWithBQ) {
        const { data: { meta: { total } } } = await fetchWithBQ(
          { url: `/external/attributes/${attributeId}/attribute-options?per_page=5000&page=1` },
        );
        const numberOfRequests = Math.ceil(total / 5000);

        const promises = [];
        for (let i = 0; i < numberOfRequests; i++) {
          promises.push(axiosInstance.get(
            `/external/attributes/${attributeId}/attribute-options?per_page=5000&page=${i + 1}`,
          ));
        }

        const response = await Promise.all(promises);
        const options = response.flatMap(res => res.data.data);

        const normalizedData = optionsAdapter.setAll(
          optionsInitialState,
          options.map(option => pickAndRename(option, BE_TO_FE_INTERFACES.Option)),
        );

        return {
          data: {
            entities: normalizedData.entities,
            ids: normalizedData.ids,
          },
        };
      },
      providesTags: (result, error, attributeId) => [{ type: 'Options', id: attributeId }],
    }),
    exportAttributes: builder.mutation({
      query: data => ({
        url: `/attributes/export`,
        method: 'POST',
        data,
      }),
    }),
  }),
});

export const selectAttributesResult = state => state.attributesApi.queries['getAttributes(undefined)']?.data || attributesAdapter.getInitialState();

export const {
  selectAll: selectAllAttributesSelector,
  selectById: selectAttributeByIdSelector,
  selectEntities: selectAllAttributesEntitiesSelector,
} = attributesAdapter.getSelectors(state => selectAttributesResult(state));

const selectOptionsResult = state => state.attributesApi.queries;
export const selectOptionsForAttributeSelector = createSelector(
  selectOptionsResult,
  (_, attributeId) => attributeId,
  (queries, attributeId) => {
    const key = `getAttributeOptions("${attributeId}")`;

    return queries[key]?.data?.entities || {};
  },
);

export const selectAttributeOptionsMap = createSelector(
  selectOptionsResult,
  (queries) => {
    const attributeOptionsMap = {};

    Object.keys(queries).forEach((key) => {
      const match = key.match(/getAttributeOptions\("([^"]+)"\)/);

      if (match) {
        const [, attributeId] = match;
        attributeOptionsMap[attributeId] = Object.values(queries[key]?.data?.entities || {});
      }
    });

    return attributeOptionsMap;
  },
);

export const {
  useGetAttributesQuery,
  useLazyGetPaginatedAttributesQuery,
  useGetAttributeOptionsQuery,
  useLazyGetAttributeOptionsQuery,
  useLazyGetExternalAttributeOptionsQuery,
  useCreateAttributeMutation,
  useUpdateAttributeMutation,
  useDeleteAttributeMutation,
  useExportAttributesMutation,
} = attributesApi;
